import React, { forwardRef, useMemo } from 'react';

import { FormattedMessage } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, Pressable, Text, xcss } from '@atlaskit/primitives';

import { type SocialSignalType } from '../../../../common/types/social-signals';
import { Avatar } from '../../../../common/ui/avatar';

import { personCardMessages } from './messages';

interface ShortInlineSocialSignalProps {
	/**
	 * The name for the user.
	 * **NOTE:** only the first name will be displayed.
	 **/
	userName: string;
	avatarUrl: string;
	onClick?: VoidFunction;
	socialSignalType: SocialSignalType;
	testId?: string;
	componentName?: string;
	analyticsContext?: Record<string, any>;
}

const signalTextXcss = xcss({
	color: 'color.text.subtlest',
	':hover': {
		textDecoration: 'underline',
	},
	':focus-visible': {
		textDecoration: 'underline',
	},
	background: 'none',
	padding: '0',
});

export const ShortInlineSocialSignal: React.ForwardRefExoticComponent<
	ShortInlineSocialSignalProps & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, ShortInlineSocialSignalProps>(
	(
		{ avatarUrl, userName, onClick, socialSignalType, testId, componentName, analyticsContext },
		ref,
	) => {
		const firstName = useMemo(() => userName.split(' ')[0], [userName]);

		const message = personCardMessages[socialSignalType];
		if (!message) {
			return null;
		}

		return (
			<Pressable
				ref={ref}
				onClick={onClick}
				xcss={signalTextXcss}
				testId={testId}
				componentName={componentName}
				analyticsContext={analyticsContext}
			>
				<Flex gap="space.075" alignItems="center">
					<Avatar variant={{ type: 'profile-pic', src: avatarUrl, name: userName }} size="xsmall" />
					<Text color="color.text.subtlest">
						<FormattedMessage {...message} values={{ name: firstName }} />
					</Text>
				</Flex>
			</Pressable>
		);
	},
);
